import { FLEET_SAAS_HUB, fleetHubPath, logisticsCityPath } from './seoUrlManifest';

export const contact = {
  phone: '+91 6357017016',
  phoneRaw: '916357017016',
  email: 'contact@fastwhistle.com',
  address: '220, 3rd Floor, Highfield Ascot, VIP Rd, opp. Palm Avenue, Vesu, Surat, Gujarat 395007',
  mapEmbed:
    'https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d14885.065992916207!2d72.7802077!3d21.1417901!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be053e1b207f21f%3A0x3884f36d71f30574!2sFast%20Whistle!5e0!3m2!1sen!2sin!4v1674873950361!5m2!1sen!2sin',
  careersPath: '/careers',
};

export const whatsappLink = (text = 'Hi, I want to know more about FastWhistle') =>
  `https://wa.me/${contact.phoneRaw}?text=${encodeURIComponent(text)}`;

export const social = {
  twitter: 'https://twitter.com/fast_whistle',
  facebook: 'https://www.facebook.com/Fastwhistle',
  instagram: 'https://www.instagram.com/fastwhistle',
  linkedin: 'https://www.linkedin.com/company/fast-whistle',
};

/**
 * Site-wide copy, nav, and footer links.
 * Canonical URL paths live in seoUrlManifest.js — import from there for links.
 */

export const LOGISTICS_CITIES_LABEL = 'Ahmedabad, Surat, Pune & Mumbai';

/**
 * One menu for every page. Logistics and Fleet SaaS open a dropdown with their page sections
 * and their spoke pages (cities / features), so the main nav never changes from page to page.
 */
export const navMenu = [
  {
    label: 'Logistics',
    to: '/logistics',
    blurb: 'Our riders deliver for you — same day.',
    sections: [
      { label: 'Overview', to: '/logistics#overview', icon: 'truck' },
      { label: 'How it works', to: '/logistics#how-it-works', icon: 'pickup' },
      { label: 'Features', to: '/logistics#features', icon: 'delivered' },
      { label: 'Screenshots', to: '/logistics#screenshots', icon: 'laptop' },
      { label: 'Cities', to: '/logistics#cities', icon: 'pin' },
      { label: 'FAQ', to: '/logistics#faq', icon: 'chat' },
    ],
    more: {
      title: 'Delivery cities',
      links: [
        { label: 'Surat', to: '/logistics/surat', note: 'Live · HQ' },
        { label: 'Ahmedabad', to: '/logistics/ahmedabad', note: 'Live' },
        { label: 'Pune', to: '/logistics/pune', note: 'Live' },
        { label: 'Mumbai', to: '/logistics/mumbai', note: 'Expanding' },
      ],
    },
  },
  {
    label: 'Fleet SaaS',
    to: FLEET_SAAS_HUB,
    blurb: 'Software to run your own field team.',
    sections: [
      { label: 'Overview', to: `${FLEET_SAAS_HUB}#overview`, icon: 'phone' },
      { label: 'How it works', to: `${FLEET_SAAS_HUB}#how-it-works`, icon: 'checklist' },
      { label: 'Features', to: `${FLEET_SAAS_HUB}#features`, icon: 'cog' },
      { label: 'Screenshots', to: `${FLEET_SAAS_HUB}#screenshots`, icon: 'laptop' },
      { label: 'Coverage', to: `${FLEET_SAAS_HUB}#coverage`, icon: 'globe' },
      { label: 'FAQ', to: `${FLEET_SAAS_HUB}#faq`, icon: 'chat' },
    ],
    more: {
      title: 'Popular features',
      links: [
        { label: 'Salesman tracking', to: `${FLEET_SAAS_HUB}/pharma-salesman-tracking-app` },
        { label: 'Attendance & TADA', to: `${FLEET_SAAS_HUB}/pharma-field-attendance-tada` },
        { label: 'Collections', to: `${FLEET_SAAS_HUB}/pharma-collections-management` },
        { label: 'ZeroMDS sync', to: `${FLEET_SAAS_HUB}/zeromds-accounting-sync` },
        { label: 'Surat hub', to: '/hubs/pharma-fleet-management-surat' },
      ],
    },
  },
  { label: 'Platform', to: '/#platform' },
  { label: 'Cities', to: '/#cities' },
  { label: 'Careers', to: '/careers' },
  { label: 'Contact', to: '/contact-us' },
];

export const homePositioning = {
  tagline:
    `Managed logistics in ${LOGISTICS_CITIES_LABEL}, and fleet management SaaS for medicine distributors across India.`,
  headline: 'Pharma Distribution, Two Ways to Run It',
  subheadline:
    `FastWhistle offers same-day delivery with our riders in ${LOGISTICS_CITIES_LABEL} (Logistics), or software to manage your own field team anywhere in India — tasks, tracking, attendance, and collections (Fleet SaaS).`,
  badges: [`Logistics: ${LOGISTICS_CITIES_LABEL}`, 'Fleet SaaS: Pan-India', 'Built for Medicine Distributors'],
};

export { homeProducts, logisticsProduct, fleetProduct } from './products';

export const positioning = homePositioning;

export const heroTrust = [
  { icon: 'Buildings', label: 'Distributor Panel' },
  { icon: 'DeviceMobile', label: 'Fleet App' },
  { icon: 'ArrowsClockwise', label: 'Accounting Sync' },
];

export const challenges = [
  {
    icon: 'MapTrifold',
    title: 'No visibility on field deliveries',
    pain: 'Orders go out but you cannot see which rider is where, or whether a delivery is still pending.',
  },
  {
    icon: 'Fingerprint',
    title: 'Attendance and field accountability',
    pain: 'Field staff attendance is hard to verify, and daily productivity is difficult to measure.',
  },
  {
    icon: 'CurrencyInr',
    title: 'Outstanding and COD tracking',
    pain: 'Collections from retail stores and parties need to be recorded accurately and reconciled with distributor records.',
  },
  {
    icon: 'ArrowsClockwise',
    title: 'Manual data entry from accounting',
    pain: 'Parties, products, orders, and outstandings often need to be re-entered instead of flowing from your accounting software.',
  },
];

export const solutions = [
  {
    icon: 'MapTrifold',
    problem: 'No delivery visibility',
    solution: 'Live rider routes and delivery status on the distributor panel',
    detail:
      'Track fleet deliveries and rider activity on a live map. See order status from dispatch to completion.',
  },
  {
    icon: 'Fingerprint',
    problem: 'Attendance gaps',
    solution: 'Geo attendance, leave, and payroll rules in fleet HR',
    detail:
      'Fleet users mark attendance from the mobile app. Attendance rules, leave, and payroll are managed from the distributor panel.',
  },
  {
    icon: 'HandCoins',
    problem: 'Collection tracking',
    solution: 'Outstanding and cash collection through the fleet app',
    detail:
      'Fleet users collect outstanding and COD from the field. Collections sync back to distributor masters and cash custody.',
  },
  {
    icon: 'Plugs',
    problem: 'Disconnected accounting data',
    solution: 'Direct sync with ZeroMDS and compatible accounting software',
    detail:
      'Import parties, products, stocks, product orders, and outstandings from your accounting software into FastWhistle.',
  },
];

export const platformPillars = [
  {
    icon: 'Desktop',
    title: 'Distributor Panel',
    description: 'Web dashboard for medicine distributors to manage orders, fleet, collections, and reporting.',
    bullets: [
      'Orders, draft orders, and fleet deliveries',
      'Cash custody and collection oversight',
      'Fleet reporting, KPIs, and activity routes',
      'Communication and event notifications',
    ],
  },
  {
    icon: 'DeviceMobile',
    title: 'Fleet Mobile App',
    description: 'Android app for distributor employees — attendance, delivery, and field collections.',
    bullets: [
      'Attendance, leave, and daily work tracking',
      'Fleet order delivery execution',
      'Outstanding and cash collection in the field',
      'Route activity and productivity timeline',
    ],
  },
  {
    icon: 'Plugs',
    title: 'Accounting Integration',
    description: 'Connect ZeroMDS or compatible accounting software to keep masters and orders in sync.',
    bullets: [
      'Sync parties, products, and stocks',
      'Import product orders and outstandings',
      'Reduce manual re-entry of billing data',
      'Keep distributor records aligned with accounting',
    ],
  },
];

export const featureModules = [
  {
    id: 'delivery',
    tab: 'Delivery',
    label: 'Delivery Operations',
    tagline: 'Dispatch, track, and complete distributor deliveries.',
    accentColor: 'blue',
    features: [
      { icon: 'Truck', title: 'Fleet Orders', benefit: 'Manage fleet draft orders and active delivery lists from the distributor panel.' },
      { icon: 'Package', title: 'Order Dispatch', benefit: 'Assign and monitor deliveries to retail stores, doctors, and stockists.' },
      { icon: 'MapPin', title: 'Rider Activities', benefit: 'View rider route timelines and live map trails for field visibility.' },
      { icon: 'Clock', title: 'Realtime Status', benefit: 'Track delivery progress from assignment through completion.' },
      { icon: 'ListChecks', title: 'Draft Orders', benefit: 'Prepare fleet draft orders before dispatch to the field.' },
      { icon: 'ChartBar', title: 'Delivery Reporting', benefit: 'Fleet reporting center with delivery KPIs and analytics.' },
    ],
  },
  {
    id: 'workforce',
    tab: 'Workforce',
    label: 'Fleet Workforce',
    tagline: 'Attendance, leave, payroll, and field team management.',
    accentColor: 'teal',
    features: [
      { icon: 'CalendarCheck', title: 'Attendance', benefit: 'Fleet users mark attendance from the mobile app; managers review reports.' },
      { icon: 'Gear', title: 'Attendance Rules', benefit: 'Configure attendance policies and rules for your field team.' },
      { icon: 'CalendarBlank', title: 'Leave Management', benefit: 'Apply and track leave for fleet employees.' },
      { icon: 'Wallet', title: 'Payroll Management', benefit: 'Fleet HR hub for payroll, leave, TADA, and related reports.' },
      { icon: 'Receipt', title: 'Daily Expenses', benefit: 'Track and approve daily field expenses.' },
      { icon: 'Users', title: 'Employee Management', benefit: 'Manage fleet employees linked to your distributor account.' },
    ],
  },
  {
    id: 'collections',
    tab: 'Collections',
    label: 'Collections & Outstandings',
    tagline: 'Track outstanding bills and field collections in one place.',
    accentColor: 'violet',
    features: [
      { icon: 'ChartLine', title: 'Outstandings Master', benefit: 'View outstanding bills and balances synced from accounting or masters.' },
      { icon: 'HandCoins', title: 'Payment Collection', benefit: 'Fleet users collect payments from parties in the field via the app.' },
      { icon: 'Vault', title: 'Cash Custody', benefit: 'Order cash custody dashboard for settlement and transfer tracking.' },
      { icon: 'FileText', title: 'Receipt Info', benefit: 'Receipt and collection activity logged against parties.' },
      { icon: 'CreditCard', title: 'Credit Notes', benefit: 'Manage credit notes alongside outstanding balances.' },
      { icon: 'Target', title: 'Auto Allocation', benefit: 'Auto allocation rules for outstanding and task assignment.' },
    ],
  },
  {
    id: 'accounting',
    tab: 'Accounting Sync',
    label: 'Accounting Software Sync',
    tagline: 'Keep FastWhistle aligned with your accounting system.',
    accentColor: 'amber',
    features: [
      { icon: 'ArrowsClockwise', title: 'Parties Sync', benefit: 'Import party master data from ZeroMDS or connected accounting software.' },
      { icon: 'Database', title: 'Products & Stocks', benefit: 'Sync product catalog and stock levels from your accounting system.' },
      { icon: 'FileText', title: 'Product Orders', benefit: 'Fetch product orders directly from accounting for delivery planning.' },
      { icon: 'ChartLine', title: 'Outstandings Import', benefit: 'Import outstanding bills to align collections with accounting records.' },
      { icon: 'Plugs', title: 'Integration API', benefit: 'Secure integration endpoints for accounting software connectivity.' },
      { icon: 'Buildings', title: 'Distributor Setup', benefit: 'Configure accounting software connection from distributor profile settings.' },
    ],
  },
];

export const howItWorksSteps = [
  {
    step: '01',
    icon: 'ArrowsClockwise',
    title: 'Sync from Accounting',
    description: 'Parties, products, orders, and outstandings flow from ZeroMDS or your connected accounting software.',
  },
  {
    step: '02',
    icon: 'ClipboardText',
    title: 'Plan & Assign',
    description: 'Create and assign fleet deliveries from the distributor panel based on synced orders.',
  },
  {
    step: '03',
    icon: 'Truck',
    title: 'Field Execution',
    description: 'Fleet users mark attendance, execute deliveries to retail stores, doctors, and stockists.',
  },
  {
    step: '04',
    icon: 'HandCoins',
    title: 'Collect & Record',
    description: 'Outstanding and COD collected in the fleet app; cash custody tracked on the panel.',
  },
  {
    step: '05',
    icon: 'ChartBar',
    title: 'Track & Report',
    description: 'Review activity routes, productivity, attendance, and delivery reports on the distributor panel.',
  },
];

export const productCapabilities = [
  { icon: 'MapTrifold', label: 'Live rider map' },
  { icon: 'ChartLine', label: 'Outstanding overview' },
  { icon: 'CalendarCheck', label: 'Attendance reports' },
  { icon: 'Package', label: 'Fleet order tracking' },
  { icon: 'Vault', label: 'Cash custody dashboard' },
];

export const cities = [
  {
    name: 'Surat',
    state: 'Gujarat',
    status: 'active',
    since: 'Logistics live',
    description: 'Same-day managed parcel delivery for Surat medicine distributors via FastWhistle riders.',
  },
  {
    name: 'Ahmedabad',
    state: 'Gujarat',
    status: 'active',
    since: 'Logistics live',
    description: 'Same-day managed parcel delivery for Ahmedabad medicine distributors via FastWhistle riders.',
  },
  {
    name: 'Pune',
    state: 'Maharashtra',
    status: 'active',
    since: 'Logistics live',
    description: 'Same-day managed B2B medicine delivery for Pune pharma distributors.',
  },
  {
    name: 'Mumbai',
    state: 'Maharashtra',
    status: 'active',
    since: 'Logistics live',
    description: 'Same-day managed B2B medicine delivery across Mumbai and Navi Mumbai distributors.',
  },
];

export const fleetCoverage = {
  title: 'Pan-India',
  eyebrow: 'Fleet SaaS coverage',
  description:
    'Fleet SaaS is available for medicine distributors across India. Onboard your salesmen, riders, and field staff from any city — distributor panel, mobile app, attendance, tasks, and accounting sync.',
  highlights: [
    'Available in all Indian cities',
    'Your employees, your operations',
    'Remote onboarding and support',
  ],
};

export const outcomes = [
  {
    icon: 'MapPin',
    statement: 'Distributors track fleet deliveries and rider routes in real time from one panel.',
  },
  {
    icon: 'Fingerprint',
    statement: 'Field teams mark attendance and execute daily work from a single fleet app.',
  },
  {
    icon: 'HandCoins',
    statement: 'Outstanding and cash collections are recorded in the field and visible to management.',
  },
  {
    icon: 'ArrowsClockwise',
    statement: 'Accounting data flows into FastWhistle so teams spend less time on manual entry.',
  },
];

export const teamStory = {
  title: 'Built in Surat for Indian Pharma Distribution',
  description:
    'FastWhistle is developed by a team that works closely with medicine distributors in Gujarat. We built the platform around real distributor workflows — delivery, fleet workforce, collections, and accounting sync — not generic courier software.',
  highlights: [
    'Focused on medicine distributor logistics',
    'Distributor panel + fleet mobile app',
    `Logistics in ${LOGISTICS_CITIES_LABEL} · Fleet SaaS pan-India`,
  ],
};

export const whiteLabel = {
  title: 'Partner With FastWhistle',
  description:
    'Distributors and networks interested in branded fleet operations or extended rollout can discuss partnership options with our team.',
  features: [
    { icon: 'PaintBrushBroad', text: 'Branded fleet experience for your network' },
    { icon: 'DeviceMobile', text: 'Mobile app for your field teams' },
    { icon: 'ShieldCheck', text: 'Dedicated distributor data and access' },
    { icon: 'UsersThree', text: 'Support for multi-party distribution networks' },
    { icon: 'Desktop', text: 'Admin panel for your operations team' },
    { icon: 'Headset', text: 'Onboarding and implementation support' },
  ],
};

export const homeFaqItems = [
  {
    question: 'What is FastWhistle?',
    answer:
      'FastWhistle offers two products: Logistics (managed same-day delivery with FastWhistle riders, per-parcel pricing — live in Ahmedabad, Surat, Pune, and Mumbai) and Fleet SaaS (software to manage your own employees — tasks, tracking, attendance, collections, and accounting sync — available pan-India).',
  },
  {
    question: 'What is the difference between Logistics and Fleet SaaS?',
    answer:
      'Logistics: we deliver your parcels with our riders in Ahmedabad, Surat, Pune, and Mumbai — you do not manage fleet staff. Fleet SaaS: you manage your own salesmen/riders anywhere in India with our app and distributor panel — tasks, live location, attendance, leave, TADA, and collections.',
  },
  {
    question: 'Does FastWhistle offer medicine delivery service in Surat?',
    answer:
      'Yes. FastWhistle provides same-day medicine delivery service in Surat for pharmaceutical distributors. Our riders deliver to chemists, doctors, and stockists with live tracking and per-parcel pricing.',
  },
  {
    question: 'What is the best fleet service in Surat for medicine distributors?',
    answer:
      'FastWhistle Fleet SaaS is built for pharma distributors in Surat and across India — live GPS tracking, attendance, order and collection tasks, and accounting sync from one distributor panel and fleet mobile app.',
  },
  {
    question: 'Is FastWhistle a medical delivery service for patients?',
    answer:
      'FastWhistle is a B2B medical delivery service for medicine distributors — delivering pharma parcels to retail pharmacies, clinics, and stockists. We are not a consumer medicine home-delivery app.',
  },
  {
    question: 'Can I use both products?',
    answer:
      'Yes. Many distributors use Logistics for outsourced delivery in our live cities and Fleet SaaS for their own field team anywhere in India, or choose the one that fits their operation.',
  },
  {
    question: 'Which cities are covered?',
    answer:
      'Logistics (managed delivery) is live in Ahmedabad, Surat, Pune, and Mumbai. Fleet SaaS is available pan-India for medicine distributors — you can onboard from any city in India.',
  },
  {
    question: 'How can I get started?',
    answer:
      'Visit the Logistics or Fleet SaaS page for details, or contact us at +91 6357017016 / WhatsApp. We will guide you based on whether you need delivery service, fleet software, or both.',
  },
];

export const faqItems = homeFaqItems;

export const footerLinks = {
  Products: [
    { label: 'Logistics (Delivery)', to: '/logistics' },
    { label: 'Fleet SaaS', to: FLEET_SAAS_HUB },
    { label: 'Compare products', to: '/compare/logistics-vs-fleet-saas' },
    { label: 'Careers', to: '/careers' },
  ],
  Company: [
    { label: 'Home', to: '/' },
    { label: 'Contact Us', to: '/contact-us' },
    { label: 'Cities We Serve', hash: '#cities' },
    { label: 'FAQ', hash: '#faq' },
  ],
  Services: [
    { label: 'Medical Delivery Service', to: '/medical-delivery-service' },
    { label: 'Medicine Delivery Surat', to: logisticsCityPath('surat') },
    { label: 'Medicine Delivery Mumbai', to: logisticsCityPath('mumbai') },
    { label: 'Fleet SaaS — Surat hub', to: fleetHubPath('surat') },
  ],
  Legal: [
    { label: 'Privacy Policy', to: '/privacy-policy' },
    { label: 'Terms & Conditions', to: '/terms-condition' },
    { label: 'Fleet App Privacy', to: '/fleet-privacy-policy' },
    { label: 'Distributor App Privacy', to: '/distributor-privacy-policy' },
    { label: 'Retailers App Privacy', to: '/retailers-privacy-policy' },
    { label: 'Rider App Privacy', to: '/rider-privacy-policy' },
  ],
  'Partner Apps': [
    { label: 'Chakradhar Privacy', to: '/chakradhar_privacy_policy.html' },
    { label: 'Pavan Distributor Privacy', to: '/pavan_distributor_privacy_policy.html' },
    { label: 'ZeroMeds Privacy', to: '/zeromeds_privacy_policy.html' },
  ],
};
